import { useEffect, useState } from "react";
import styles from "./Download.module.css";

/**
 * Download — fetches the suite release manifest at
 * https://assets.atlasdesk.co/suite/latest.json on mount and wires the
 * button to the .dmg URL it advertises. Version, filename, and size all
 * come from the manifest so the page never drifts from what's actually
 * shipping.
 *
 * While the fetch is in flight (or if it fails — e.g., very first visit
 * before the first release ships), the button stays in a disabled
 * "preparing your download" state with a quiet placeholder.
 */

const MANIFEST_URL = "https://assets.atlasdesk.co/suite/latest.json";

interface ReleaseManifest {
  version: string;
  notes?: string;
  pub_date?: string;
  downloads?: {
    "darwin-universal"?: {
      url: string;
      filename: string;
      size: number;
    };
  };
}

export function Download() {
  const [release, setRelease] = useState<ReleaseManifest | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(MANIFEST_URL, { cache: "no-cache" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<ReleaseManifest>;
      })
      .then((data) => {
        if (!cancelled) setRelease(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dmg = release?.downloads?.["darwin-universal"] ?? null;
  const ready = dmg !== null;
  const version = release?.version ?? "—";

  return (
    <section
      id="download"
      className={styles.section}
      aria-labelledby="download-title"
    >
      <div className={`atlasContainer ${styles.inner}`}>
        <header className={styles.header}>
          <span className="atlasEyebrow">Installer</span>
          <h2 id="download-title" className={styles.title}>
            Bring Atlas Desk home.
          </h2>
          <p className={styles.lede}>
            One file. Drop it in Applications. Open it. Done.
          </p>
        </header>

        <article className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.cardHeadLeft}>
              <span className={styles.platformDot} aria-hidden />
              macOS
            </span>
            <span className={styles.versionTag}>
              {ready ? `v${version} — Preview` : failed ? "Soon" : "Loading…"}
            </span>
          </div>

          <div className={styles.cardBody}>
            <a
              href={dmg?.url ?? "#"}
              className={`${styles.downloadBtn} ${!ready ? styles.downloadBtnDisabled : ""}`}
              aria-disabled={!ready}
              aria-label={
                ready
                  ? `Download Atlas Desk v${version} for macOS`
                  : "Download not yet available"
              }
              onClick={(e) => {
                if (!ready) e.preventDefault();
              }}
              download={dmg?.filename}
            >
              <span className={styles.downloadBtnIcon} aria-hidden>
                <DownloadGlyph />
              </span>
              <span className={styles.downloadBtnText}>
                <span className={styles.downloadBtnLabel}>
                  {ready ? "Download Atlas Desk" : "Preparing your download…"}
                </span>
                <span className={styles.downloadBtnMeta}>
                  {ready && dmg
                    ? `${dmg.filename} · ${formatBytes(dmg.size)} · macOS 13+`
                    : failed
                      ? "First release is shipping shortly"
                      : "Fetching the latest build…"}
                </span>
              </span>
              <span className={styles.downloadBtnArrow} aria-hidden>
                ↓
              </span>
            </a>

            <div className={styles.altRow}>
              <span className={styles.altLabel}>Coming soon</span>
              <span className={styles.altPlatform}>Linux</span>
              <span className={styles.altSep}>·</span>
              <span className={styles.altPlatform}>Windows</span>
            </div>

          </div>

          <footer className={styles.cardFoot}>
            <span className={styles.cardFootLabel}>What's next</span>
            <p className={styles.cardFootTeaser}>
              More apps.
              <br />
              Same simplicity.
            </p>
          </footer>
        </article>
      </div>
    </section>
  );
}

function DownloadGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3 L12 16 M6 11 L12 17 L18 11 M4 20 L20 20"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatBytes(n: number): string {
  if (n >= 1024 * 1024 * 1024)
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}
